import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useSWR from 'swr';
import { useSearchParams } from 'react-router-dom';
import { Check, ChevronDown, Loader2, Search, Video } from 'lucide-react';
import { listCameras, CameraDto } from '../api/cameras';
import { useAppActions, useAppState } from '../app/appContext';
import { useT } from '@/contexts/I18nContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/tooltip';

const statusDot = (cam: CameraDto): { color: string; label: string } => {
  if (!cam.active) return { color: '#c4c4c4', label: 'camera.inactive' };
  if (!cam.online) return { color: '#5f6368', label: 'camera.offline' };
  return { color: '#43a047', label: 'camera.online' };
};

const CameraSelector: React.FC = () => {
  const { t } = useT();
  const { setCamera, setSelectedCamera } = useAppActions();
  const { camera, zone } = useAppState();
  const isNarrow = useMediaQuery('(max-width: 768px)');
  const selectedCameraId = camera.selectedCameraId;
  const selectedCameraIdStr = selectedCameraId != null ? String(selectedCameraId) : '';
  const selectedZoneId = zone.selectedZoneId;
  const [, setSearchParams] = useSearchParams();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuPos, setMenuPos] = useState<{ top: number; right: number; minWidth: number; maxWidth: number } | null>(null);
  const [hlIndex, setHlIndex] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const syncCameraParam = useCallback((cameraId: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (cameraId) next.set('camera', cameraId); else next.delete('camera');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const { data: cameras, isLoading, mutate } = useSWR(
    selectedZoneId ? `cameras:list:1000:${selectedZoneId}` : 'cameras:list:1000:no-zone',
    () => listCameras(1000, selectedZoneId || undefined).then(r => r.data.results),
  );

  const sortedCameras = cameras ? [...cameras].sort((a: CameraDto, b: CameraDto) => {
    const aOk = a.active && a.online;
    const bOk = b.active && b.online;
    return aOk === bOk ? 0 : aOk ? -1 : 1;
  }) : [];

  const selectedCamera = sortedCameras.find((c: CameraDto) => String(c.id) === selectedCameraIdStr) ?? null;

  React.useEffect(() => {
    if (selectedCameraIdStr && selectedCamera) {
      setSelectedCamera(selectedCamera);
    } else if (!selectedCameraIdStr) {
      setSelectedCamera(null);
    }
  }, [selectedCameraIdStr, selectedCamera, setSelectedCamera]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Position menu under trigger, with viewport collision detection.
  // The trigger may be narrower than the menu's content (long camera names);
  // fix-anchor-left used to overflow the viewport. We now: (a) cap the menu
  // width at the viewport, (b) shift left if the right edge would clip,
  // (c) flip up if the bottom would clip.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const compute = () => {
      const r = triggerRef.current!.getBoundingClientRect();
      const vpW = window.innerWidth;
      const vpH = window.innerHeight;
      const PAD = 8;
      // Anchor the menu's RIGHT edge to the trigger's right edge so it
      // expands leftward when option names are wider than the trigger.
      // No fixed width: let auto-width handle long camera names, capped
      // at viewport so we never overflow.
      const right = Math.max(PAD, vpW - r.right);
      const minWidth = r.width;
      const maxWidth = vpW - right - PAD;

      const estHeight = Math.min(vpH * 0.6, 360);
      const fitsBelow = r.bottom + 4 + estHeight <= vpH - PAD;
      const top = fitsBelow ? r.bottom + 4 : Math.max(PAD, r.top - 4 - estHeight);

      setMenuPos({ top, right, minWidth, maxWidth });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open]);

  const filtered = sortedCameras.filter((c: CameraDto) =>
    c.name.toLowerCase().includes(query.trim().toLowerCase())
  );

  useEffect(() => {
    setHlIndex(i => (filtered.length === 0 ? 0 : Math.min(i, filtered.length - 1)));
  }, [filtered.length]);

  useEffect(() => { setHlIndex(0); }, [query, open]);

  useEffect(() => {
    if (!open) return;
    itemRefs.current[hlIndex]?.scrollIntoView({ block: 'nearest' });
  }, [hlIndex, open]);

  const handlePick = (cam: CameraDto) => {
    const nextId = String(cam.id);
    setCamera(nextId);
    syncCameraParam(nextId);
    setSelectedCamera(cam);
    setOpen(false);
    setQuery('');
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHlIndex(i => (filtered.length === 0 ? 0 : Math.min(i + 1, filtered.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHlIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filtered.length > 0) {
      e.preventDefault();
      handlePick(filtered[Math.min(hlIndex, filtered.length - 1)]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  const handleOpen = () => {
    if (!selectedZoneId) return;
    mutate();
    setOpen(o => !o);
  };

  const disabled = !selectedZoneId;

  return (
    <>
      <Tooltip content={isNarrow ? (selectedCamera?.name || t('camera.placeholder')) : undefined}><button
        ref={triggerRef}
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        className={cn(
          'inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-card text-sm shadow-sm transition-colors',
          'hover:border-primary/40 hover:bg-card hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
          // Narrow: fill the flex-1 wrapper (which is the space left after the
          // left controls) and truncate the name; min-w-0 lets it shrink to
          // the icon when space is very tight, so it never overruns the icons.
          // Wide: keep the comfortable fixed minimum, sized to content.
          isNarrow ? 'w-full min-w-0 max-w-full px-2' : 'min-w-[160px] px-3',
          open && 'border-primary/50 ring-2 ring-ring/30',
          disabled && 'opacity-55 cursor-not-allowed pointer-events-none',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {isLoading
          ? <Loader2 className="size-4 animate-spin text-muted-foreground shrink-0" />
          : <Video className="size-4 text-primary shrink-0" />
        }
        <span className={cn('min-w-0 flex-1 truncate font-medium text-left', !selectedCamera && 'text-muted-foreground')}>
          {selectedCamera?.name || t('camera.placeholder')}
        </span>
        {/* Chevron hidden on narrow to reclaim space for the name. */}
        <ChevronDown className={cn('size-4 text-muted-foreground shrink-0 ml-auto', isNarrow && 'hidden')} />
      </button></Tooltip>

      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, minWidth: menuPos.minWidth, maxWidth: menuPos.maxWidth }}
          className="z-[100] flex max-h-[60vh] flex-col overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-lg animate-in fade-in zoom-in-95 duration-100"
        >
          <div className="border-b border-border p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <input
                autoFocus
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder={t('camera.search')}
                className="h-8 w-full rounded-md border border-input bg-background pl-8 pr-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:border-ring"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-1">
            {isLoading && (
              <div className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" /> {t('camera.loading')}
              </div>
            )}
            {!isLoading && filtered.length === 0 && (
              <div className="py-3 text-center text-xs text-muted-foreground">
                {query ? t('camera.notFound') : t('camera.empty')}
              </div>
            )}
            {!isLoading && filtered.map((cam: CameraDto, idx) => {
              const dot = statusDot(cam);
              const id = String(cam.id);
              const isActive = id === selectedCameraIdStr;
              const isHighlighted = idx === hlIndex;
              return (
                <Tooltip key={id} content={t(dot.label)}><button
                  ref={el => { itemRefs.current[idx] = el; }}
                  type="button"
                  onClick={() => handlePick(cam)}
                  onMouseEnter={() => setHlIndex(idx)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                    isActive
                      ? 'bg-primary-muted text-primary-muted-foreground'
                      : isHighlighted
                        ? 'bg-muted text-foreground'
                        : 'hover:bg-muted hover:text-foreground'
                  )}
                >
                  <span
                    className="size-2 shrink-0 rounded-full"
                    style={{ background: dot.color }}
                  />
                  <span className="flex-1 whitespace-nowrap">{cam.name}</span>
                  {isActive && <Check className="size-4 shrink-0" />}
                </button></Tooltip>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CameraSelector;
